import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useUserCountry() {
  const { user } = useAuth();
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectCountry = async () => {
      setIsLoading(true);

      try {
        // First, check if user has a preferred country in their profile
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("preferred_country_code")
            .eq("user_id", user.id)
            .maybeSingle();

          if (profile?.preferred_country_code) {
            setCountryCode(profile.preferred_country_code);
            setIsLoading(false);
            return;
          }
        }

        // Check localStorage for cached country
        const cachedCountry = localStorage.getItem("user_country_code");
        const cachedExpiry = localStorage.getItem("user_country_expiry");
        
        if (cachedCountry && cachedExpiry && Date.now() < parseInt(cachedExpiry)) {
          setCountryCode(cachedCountry);
          setIsLoading(false);
          return;
        }

        // Use timezone to infer country (fast, no API call)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneCountry = getCountryFromTimezone(timezone);
        
        if (timezoneCountry) {
          setCountryCode(timezoneCountry);
          // Cache for 24 hours
          localStorage.setItem("user_country_code", timezoneCountry);
          localStorage.setItem("user_country_expiry", (Date.now() + 24 * 60 * 60 * 1000).toString());
        } else {
          // Fallback to US
          setCountryCode("US");
        }
      } catch (error) {
        console.error("Error detecting country:", error);
        setCountryCode("US");
      } finally {
        setIsLoading(false);
      }
    };

    detectCountry();
  }, [user]);

  return { countryCode, isLoading };
}

function getCountryFromTimezone(timezone: string): string | null {
  const timezoneToCountry: Record<string, string> = {
    // United States
    "America/New_York": "US",
    "America/Chicago": "US",
    "America/Denver": "US",
    "America/Los_Angeles": "US",
    "America/Phoenix": "US",
    "America/Anchorage": "US",
    "Pacific/Honolulu": "US",
    // United Kingdom
    "Europe/London": "GB",
    // Germany
    "Europe/Berlin": "DE",
    // France
    "Europe/Paris": "FR",
    // Spain
    "Europe/Madrid": "ES",
    // Italy
    "Europe/Rome": "IT",
    // Netherlands
    "Europe/Amsterdam": "NL",
    // Belgium
    "Europe/Brussels": "BE",
    // Austria
    "Europe/Vienna": "AT",
    // Switzerland
    "Europe/Zurich": "CH",
    // Australia
    "Australia/Sydney": "AU",
    "Australia/Melbourne": "AU",
    "Australia/Brisbane": "AU",
    "Australia/Perth": "AU",
    // Canada
    "America/Toronto": "CA",
    "America/Vancouver": "CA",
    "America/Montreal": "CA",
    // Ireland
    "Europe/Dublin": "IE",
    // Sweden
    "Europe/Stockholm": "SE",
    // Norway
    "Europe/Oslo": "NO",
    // Denmark
    "Europe/Copenhagen": "DK",
    // Finland
    "Europe/Helsinki": "FI",
    // Poland
    "Europe/Warsaw": "PL",
    // Portugal
    "Europe/Lisbon": "PT",
    // Czech Republic
    "Europe/Prague": "CZ",
    // Greece
    "Europe/Athens": "GR",
    // Hungary
    "Europe/Budapest": "HU",
    // Romania
    "Europe/Bucharest": "RO",
    // Japan
    "Asia/Tokyo": "JP",
    // Singapore
    "Asia/Singapore": "SG",
    // Hong Kong
    "Asia/Hong_Kong": "HK",
    // India
    "Asia/Kolkata": "IN",
    // New Zealand
    "Pacific/Auckland": "NZ",
    // South Africa
    "Africa/Johannesburg": "ZA",
    // Brazil
    "America/Sao_Paulo": "BR",
    // Mexico
    "America/Mexico_City": "MX",
    // Argentina
    "America/Argentina/Buenos_Aires": "AR",
  };

  return timezoneToCountry[timezone] || null;
}
